"use client";

import { useState } from "react";
import { Save, User, Settings, Plus, Minus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "../layout/Modal";
import "./EmployeeCreatePage.css";
import BackButton from "../shared/backButton";
import { useTranslation } from "react-i18next";

const EmployeeCreatePage = ({ onSave }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
    department: "",
    level: "Mid-level",
    manager: "",
    salary: "",
    workStyle: "Hybrid",
    joinDate: new Date().toISOString().split("T")[0],
    skills: [],
    education: "",
    experience: "",
    languages: [],
    interests: [],
    certifications: [],
    projects: [],
    performanceRating: "Good",
    availability: "Available",
  });

  const [newSkill, setNewSkill] = useState("");
  const [newLanguage, setNewLanguage] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [newCertification, setNewCertification] = useState("");
  const [newProject, setNewProject] = useState("");

  // Lookup data
  const departments = [
    t("common.departments.engineering"),
    t("common.departments.product"),
    t("common.departments.analytics"),
    t("common.departments.sales"),
    t("common.departments.marketing"),
    t("common.departments.design"),
    t("common.departments.humanResources"),
  ];
  const levels = [
    t("common.levels.junior"),
    t("common.levels.midLevel"),
    t("common.levels.senoir"),
    t("common.levels.lead"),
    t("common.levels.manager"),
    t("common.levels.director"),
  ];
  const workStyles = [
    t("common.workStyles.remote"),
    t("common.workStyles.hybrid"),
    t("common.workStyles.onsite"),
    t("common.workStyles.flexible"),
  ];
  const performanceRatings = [
    t("common.performanceRatings.excellent"),
    t("common.performanceRatings.good"),
    t("common.performanceRatings.average"),
    t("common.performanceRatings.needsImprovement"),
  ];
  // const availabilityOptions = [
  //   t("common.availability.available"),
  //   t("common.availability.partiallyAvailable"),
  //   t("common.availability.notAvailable"),
  // ]

  const availabilityOptions = [
    t("teamDetailPage.availability.available"),
    t("teamDetailPage.availability.partiallyAvailable"),
    t("teamDetailPage.availability.notAvailable"),
  ];

  // Sample managers for lookup
  const managers = [
    "Sarah Johnson",
    "Alex Thompson",
    "Emily Rodriguez",
    "Mike Chen",
    "Lisa Chang",
    "David Kim",
    "Jennifer Lee",
    "Carlos Rodriguez",
    "Amanda Foster",
    "Robert Davis",
    "Maria Garcia",
    "Kevin Park",
  ];

  // Sample roles for suggestions
  const commonRoles = [
    t("common.roles.softwareEngineer"),
    t("common.roles.seniorSoftwareEngineer"),
    t("common.roles.productManager"),
    t("common.roles.dataScientist"),
    t("common.roles.uxDesigner"),
    t("common.roles.marketingSpecialist"),
    t("common.roles.salesAssociate"),
    t("common.roles.hrCoordinator"),
    t("common.roles.devOpsEngineer"),
    t("common.roles.businessAnalyst"),
    t("common.roles.projectManager"),
    t("common.roles.qaEngineer"),
  ];

  // Sample locations for suggestions
  const commonLocations = [
    "San Francisco, CA",
    "New York, NY",
    "Austin, TX",
    "Seattle, WA",
    "Los Angeles, CA",
    "Chicago, IL",
    "Denver, CO",
    "Miami, FL",
    "Boston, MA",
    "Portland, OR",
    "Atlanta, GA",
    "Dallas, TX",
  ];

  const handleSubmit = (e) => {
    e.preventDefault();

    if (onSave) {
      onSave(formData);
    }

    setShowModal({
      show: true,
      type: "success",
      title: t("employeeCreate.actions.employeeCreated"),
      message: t("employeeCreate.actions.employeeAdded", {
        name: formData.name,
      }),
    });
  };

  const handleAddSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData({
        ...formData,
        skills: [...formData.skills, newSkill.trim()],
      });
      setNewSkill("");
    }
  };

  const handleRemoveSkill = (skillToRemove) => {
    setFormData({
      ...formData,
      skills: formData.skills.filter((skill) => skill !== skillToRemove),
    });
  };

  const handleAddLanguage = () => {
    if (
      newLanguage.trim() &&
      !formData.languages.includes(newLanguage.trim())
    ) {
      setFormData({
        ...formData,
        languages: [...formData.languages, newLanguage.trim()],
      });
      setNewLanguage("");
    }
  };

  const handleRemoveLanguage = (languageToRemove) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter(
        (language) => language !== languageToRemove
      ),
    });
  };

  const handleAddInterest = () => {
    if (
      newInterest.trim() &&
      !formData.interests.includes(newInterest.trim())
    ) {
      setFormData({
        ...formData,
        interests: [...formData.interests, newInterest.trim()],
      });
      setNewInterest("");
    }
  };

  const handleRemoveInterest = (interestToRemove) => {
    setFormData({
      ...formData,
      interests: formData.interests.filter(
        (interest) => interest !== interestToRemove
      ),
    });
  };

  const handleAddCertification = () => {
    if (
      newCertification.trim() &&
      !formData.certifications.includes(newCertification.trim())
    ) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCertification.trim()],
      });
      setNewCertification("");
    }
  };

  const handleRemoveCertification = (certificationToRemove) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter(
        (certification) => certification !== certificationToRemove
      ),
    });
  };

  const handleAddProject = () => {
    if (newProject.trim() && !formData.projects.includes(newProject.trim())) {
      setFormData({
        ...formData,
        projects: [...formData.projects, newProject.trim()],
      });
      setNewProject("");
    }
  };

  const handleRemoveProject = (projectToRemove) => {
    setFormData({
      ...formData,
      projects: formData.projects.filter(
        (project) => project !== projectToRemove
      ),
    });
  };

  const navigate = useNavigate();
  const onNavigate = (page) => {
    console.log("Navigating to:", page);
    navigate(page);
  };

  const handleModalClose = () => {
    setShowModal({ show: false, type: "", title: "", message: "" });
    onNavigate("employees");
  };

  return (
    <div className="employee-create-page">
      <BackButton />

      <main className="employee-create-main">
        <div className="container">
          <div className="page-header">
            <div className="header-icon">
              <User size={32} />
            </div>
            <h1>{t("employeeCreate.pageTitle")}</h1>
            <p>{t("employeeCreate.pageDescription")}</p>
          </div>

          <form onSubmit={handleSubmit} className="employee-create-form">
            <div className="form-section">
              <h2>
                <Settings size={20} />
                {t("employeeCreate.basicInformation")}
              </h2>

              <div className="form-grid">
                <div className="form-group">
                  <label>{t("employeeCreate.form.fullName")} *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    placeholder={t("employeeCreate.form.enterFullName")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.email")} *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                    placeholder={t("employeeCreate.form.enterEmail")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.phone")}</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                    placeholder={t("employeeCreate.form.enterPhone")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.role")} *</label>
                  <input
                    type="text"
                    list="roles-list"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    required
                    placeholder={t("employeeCreate.form.enterRole")}
                  />
                  <datalist id="roles-list">
                    {commonRoles.map((role, index) => (
                      <option key={index} value={role} />
                    ))}
                  </datalist>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.department")} *</label>
                  <select
                    value={formData.department}
                    onChange={(e) =>
                      setFormData({ ...formData, department: e.target.value })
                    }
                    required
                  >
                    <option value="">
                      {t("employeeCreate.form.selectDepartment")}
                    </option>
                    {departments.map((dept) => (
                      <option key={dept} value={dept}>
                        {dept}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.level")}</label>
                  <select
                    value={formData.level}
                    onChange={(e) =>
                      setFormData({ ...formData, level: e.target.value })
                    }
                  >
                    {levels.map((level) => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.manager")}</label>
                  <select
                    value={formData.manager}
                    onChange={(e) =>
                      setFormData({ ...formData, manager: e.target.value })
                    }
                  >
                    <option value="">
                      {t("employeeCreate.form.selectManager")}
                    </option>
                    {managers.map((manager) => (
                      <option key={manager} value={manager}>
                        {manager}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.salary")}</label>
                  <input
                    type="text"
                    value={formData.salary}
                    onChange={(e) =>
                      setFormData({ ...formData, salary: e.target.value })
                    }
                    placeholder={t("employeeCreate.form.salaryPlaceholder")}
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.workStyle")}</label>
                  <select
                    value={formData.workStyle}
                    onChange={(e) =>
                      setFormData({ ...formData, workStyle: e.target.value })
                    }
                  >
                    {workStyles.map((style) => (
                      <option key={style} value={style}>
                        {style}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.joinDate")}</label>
                  <input
                    type="date"
                    value={formData.joinDate}
                    onChange={(e) =>
                      setFormData({ ...formData, joinDate: e.target.value })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.performanceRating")}</label>
                  <select
                    value={formData.performanceRating}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        performanceRating: e.target.value,
                      })
                    }
                  >
                    {performanceRatings.map((rating) => (
                      <option key={rating} value={rating}>
                        {rating}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>{t("employeeCreate.form.availability")}</label>
                  <select
                    value={formData.availability}
                    onChange={(e) =>
                      setFormData({ ...formData, availability: e.target.value })
                    }
                  >
                    {availabilityOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h2>
                <User size={20} />
                {t("employeeCreate.skillsAdditionalInfo")}
              </h2>

              <div className="skills-section">
                <div className="skill-group">
                  <h3>{t("employeeCreate.skills.skills")}</h3>
                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newSkill}
                      onChange={(e) => setNewSkill(e.target.value)}
                      placeholder={t("employeeCreate.skills.addSkill")}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddSkill())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddSkill}
                    >
                      <Plus size={14} />
                      {t("employeeCreate.skills.add")}
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.skills.map((skill, index) => (
                      <span key={index} className="skill-tag">
                        {skill}
                        <button
                          type="button"
                          className="remove-skill"
                          onClick={() => handleRemoveSkill(skill)}
                        >
                          <Minus size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="skill-group">
                  <h3>{t("employeeCreate.skills.languages")}</h3>
                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newLanguage}
                      onChange={(e) => setNewLanguage(e.target.value)}
                      placeholder={t("employeeCreate.skills.addLanguage")}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddLanguage())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddLanguage}
                    >
                      <Plus size={14} />
                      {t("employeeCreate.skills.add")}
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.languages.map((language, index) => (
                      <span key={index} className="language-tag">
                        {language}
                        <button
                          type="button"
                          className="remove-skill"
                          onClick={() => handleRemoveLanguage(language)}
                        >
                          <Minus size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="skill-group">
                  <h3>{t("employeeCreate.skills.interests")}</h3>
                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newInterest}
                      onChange={(e) => setNewInterest(e.target.value)}
                      placeholder={t("employeeCreate.skills.addInterest")}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddInterest())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddInterest}
                    >
                      <Plus size={14} />
                      {t("employeeCreate.skills.add")}
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.interests.map((interest, index) => (
                      <span key={index} className="interest-tag">
                        {interest}
                        <button
                          type="button"
                          className="remove-skill"
                          onClick={() => handleRemoveInterest(interest)}
                        >
                          <Minus size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="skill-group">
                  <h3>{t("employeeCreate.skills.certifications")}</h3>
                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newCertification}
                      onChange={(e) => setNewCertification(e.target.value)}
                      placeholder={t("employeeCreate.skills.addCertification")}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddCertification())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddCertification}
                    >
                      <Plus size={14} />
                      {t("employeeCreate.skills.add")}
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.certifications.map((certification, index) => (
                      <span key={index} className="certification-tag">
                        {certification}
                        <button
                          type="button"
                          className="remove-skill"
                          onClick={() =>
                            handleRemoveCertification(certification)
                          }
                        >
                          <Minus size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>

                <div className="skill-group">
                  <h3>{t("employeeCreate.skills.projects")}</h3>
                  <div className="skill-input-group">
                    <input
                      type="text"
                      value={newProject}
                      onChange={(e) => setNewProject(e.target.value)}
                      placeholder={t("employeeCreate.skills.addProject")}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), handleAddProject())
                      }
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={handleAddProject}
                    >
                      <Plus size={14} />
                      {t("employeeCreate.skills.add")}
                    </button>
                  </div>
                  <div className="skills-tags">
                    {formData.projects.map((project, index) => (
                      <span key={index} className="project-tag">
                        {project}
                        <button
                          type="button"
                          className="remove-skill"
                          onClick={() => handleRemoveProject(project)}
                        >
                          <Minus size={12} />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => onNavigate("employees")}
              >
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn btn-primary">
                <Save size={16} />
                {t("employeeCreate.actions.createEmployee")}
              </button>
            </div>
          </form>
        </div>
      </main>

      <Modal
        show={showModal.show}
        onClose={handleModalClose}
        type={showModal.type}
        title={showModal.title}
        message={showModal.message}
      />
    </div>
  );
};

export default EmployeeCreatePage;
